import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  identifyUser,
  addUserProperties,
  addEventProperties,
  removeEventProperty,
  resetUserIdentity,
  trackEvent,
  AnalyticsEventNames,
  AnalyticsPropertyNames,
} from '../../utils/analytics';
import { getBlid } from '../../utils/cookieHelpers';

/**
 * Component to identify users in Heap Analytics
 * This component monitors the Redux user state and identifies/updates the user in Heap
 * when they log in or their properties change
 */
const HeapUserIdentifier = () => {
  const user = useSelector(state => state.user);
  const { userId, email, firstName, lastName, isAuthenticated } = user;

  useEffect(() => {
    // Only identify user if they are authenticated and we have a userId
    if (isAuthenticated && userId) {
      // Identify the user in Heap using their userId
      identifyUser(userId.toString());

      // Detect authentication type (SSO vs JWT)
      const blid = getBlid();
      const authType = blid ? 'SSO' : 'JWT';

      // Add user properties
      const userProperties = {
        userId: userId.toString(),
        email: email || '',
        name: `${firstName} ${lastName}`.trim() || '',
        firstName: firstName || '',
        lastName: lastName || '',
        authType: authType,
      };

      addUserProperties(userProperties);

      // Add BLID as an event property if it exists
      // This ensures BLID is attached to all subsequent events
      if (blid) {
        addEventProperties({ blid });
      }

      // Track login event only when:
      // 1. No login has been tracked for this session yet, OR
      // 2. User ID changed (different user logged in)
      // Using sessionStorage to persist across component remounts
      const lastTrackedUserId = sessionStorage.getItem(
        'heap_last_tracked_user_id'
      );
      const userIdString = userId.toString();

      if (lastTrackedUserId !== userIdString) {
        trackEvent(AnalyticsEventNames.USER_LOGIN, {
          userId: userIdString,
          email: email || '',
          loginMethod: authType,
        });
        sessionStorage.setItem('heap_last_tracked_user_id', userIdString);
      }
    } else if (!isAuthenticated && userId === null) {
      // User logged out - reset identity and clear tracking state
      resetUserIdentity();
      sessionStorage.removeItem('heap_last_tracked_user_id');

      // Remove BLID from event properties on logout
      removeEventProperty(AnalyticsPropertyNames.BLID);
    }
  }, [userId, email, firstName, lastName, isAuthenticated]);

  // This component doesn't render anything
  return null;
};

export default HeapUserIdentifier;
